import type { InvestorProfile, Structure } from "../types/entities";
import type { PropertyRecord } from "../types/portfolio";
import type { BudgetSettings } from "../types/budget";
import type { AppSettings } from "../components/settings/SettingsPage";
import { calculateMarginalRate, effectiveMarginalRate, formatRateComponentsLabel, personalTaxSavingFromLoss, filterDeductibleExpenseRows, annualLoanInterest, annualLoanRepayment, totalLoanBalance, propertyMetrics, portfolioFundingSummary, portfolioSummary, lvr, equityReleaseRunwaySimple, equityReleaseRunwayDynamic, computeBucketAllocations, isPersonalProperty, displayEntityLabel, deriveFamilyMlsContext, cgtDiscountRate, computeDiv43Rate, computeDiv43AnnualClaim, isDiv43BoundaryCase, computeDiv40Eligibility, computeDiv40AnnualClaim, computeLowValuePoolAnnualClaim, allocateTrustNetIncome } from "./calculations";
import { personalTrustDistributions, smsfFundSummary } from "./entityTaxData";
import { prorateFactor, saleStatusForFY } from "./saleProrate";
import { recomputeLowValuePools } from "./lowValuePool";
// B154.1 — read ONLY the loaded rules' FY label (never a figure) for the stale-rates
// disclosure. If the current FY has no rules file, the AI must relay the caveat.
import { CURRENT_TAX_RULES } from "./tax-rules-2025-26";
import { secondHandRuleApplies } from "./secondHandRule";
import { computeLensAllocatedLoss, resolveLensProfile, householdFactorOf } from "./householdScope";
import { calculateCAGR } from "./historySnapshot";
import { getCachedHtagData } from "./useHtagData";
import { getHtagType } from "./htag-property-type";

function fmt(n: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

// `secondHandRuleApplies` lifted to `src/lib/secondHandRule.ts` in Phase 11
// so TaxPage banners can drive off the same logic without importing
// buildPortfolioContext (which has a heavier dependency footprint).

// B122-P3 v2 (Day 81) — exported for entityTaxData personalTrustDistributions.
// Trustee-scope helper builds its own un-lensed propertyTaxData internally.
// Function-only circular import (entityTaxData ↔ buildPortfolioContext); both
// modules use the imported values inside function bodies, so ESM evaluation
// resolves cleanly at runtime. Same pattern as calculations.ts ↔ saleProrate.ts.
export function annualIncome(prop: PropertyRecord): number {
  if (prop.incomeRows && prop.incomeRows.length > 0) {
    return prop.incomeRows.reduce((sum, r) => {
      const mult =
        r.frequency === "weekly" ? 52 :
        r.frequency === "fortnightly" ? 26 :
        r.frequency === "monthly" ? 12 :
        1; // yearly
      return sum + r.amount * mult;
    }, 0);
  }
  return (prop.weeklyRent ?? 0) * 52;
}

export function annualExpenses(prop: PropertyRecord): number {
  if (prop.expenseRows && prop.expenseRows.length > 0) {
    // Exclude mortgage repayments — interest is computed separately. See
    // calculations.ts EXCLUDED_EXPENSE_CATEGORIES.
    return filterDeductibleExpenseRows(prop.expenseRows).reduce((sum, r) => {
      const mult =
        r.frequency === "weekly" ? 52 :
        r.frequency === "fortnightly" ? 26 :
        r.frequency === "monthly" ? 12 :
        1;
      return sum + r.amount * mult;
    }, 0);
  }
  return (
    (prop.annualRates ?? 0) +
    (prop.annualInsurance ?? 0) +
    (prop.annualStrata ?? 0) +
    (prop.annualLandTax ?? 0) +
    (prop.annualMaintenance ?? 0) +
    (prop.annualPropertyManagement ?? 0) +
    (prop.monthlyExpenses ?? 0) * 12
  );
}

// Uses shared annualLoanInterest from calculations.ts — single source of truth.

/**
 * Current FY label in the same format used by TaxPage.fyDeprecLabel and the
 * `financialYear` field on `DepreciationEntry`. Example: "2025-26".
 */
function currentFYLabel(): string {
  const now = new Date();
  const startYear = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
}

/**
 * Annual depreciation for the current FY, mirroring TaxPage.depreciationForFY.
 * Reads only from `depreciationClaimed` filtered by FY label so the AI sees the
 * same figures the user sees on the Tax page Depreciation tab.
 */
export function annualDepreciation(prop: PropertyRecord, fyLabel: string): { div40: number; div43: number; total: number } {
  const entries = (prop.depreciationClaimed ?? []).filter((e) => e.financialYear === fyLabel);
  let div40 = 0;
  let div43 = 0;
  let other = 0;
  for (const e of entries) {
    if (e.divisionType === "40") div40 += e.amountClaimed;
    else if (e.divisionType === "43") div43 += e.amountClaimed;
    else other += e.amountClaimed;
  }
  return { div40, div43, total: div40 + div43 + other };
}

export function buildPortfolioContext(
  profile: InvestorProfile,
  properties: PropertyRecord[],
  structures: Structure[],
  includeSMSFInPortfolio = true,
  budgetSettings?: BudgetSettings,
  appSettings?: AppSettings,
  monthlySurplus?: number,
): string {
  // Filter out draft scenario properties
  const activeProperties = properties.filter(
    (p) => !p.notes?.includes("[DRAFT SCENARIO]")
  );

  // Profile section
  const name = profile.preferredName ?? profile.fullLegalName ?? "Investor";
  const income = profile.annualPreTaxIncome ?? 0;
  const marginalRate = profile.marginalTaxRate ?? calculateMarginalRate(income);
  // B116 — derive family-MLS context once and thread through every rate
  // helper here so the AI sees the same MLS regime as the underlying math.
  // B119 follow-up — household-level `effMarginalRate` deleted; was only
  // consumed by the now-removed "Estimated personal tax saving at X%"
  // line in personalTaxPositionSection. Per-adult effective rates live on the
  // `adultEffRate` local inside perAdultTaxAllocationSection (single SSOT
  // for tax-saving emissions).
  const familyMlsCtx = deriveFamilyMlsContext(profile);
  const tone = profile.strategyTone ?? "Balanced";
  const passiveGoal = profile.passiveIncomeTarget;
  const valueGoal = profile.portfolioValueTarget;

  let profileSection = `INVESTOR PROFILE
Name: ${name}
Annual income: ${fmt(income)}
Marginal tax rate: ${marginalRate}%
Strategy tone: ${tone}`;
  if (passiveGoal) profileSection += `\nPassive income goal: ${fmt(passiveGoal)}/yr`;
  if (valueGoal) profileSection += `\nPortfolio value goal: ${fmt(valueGoal)}`;
  if (profile.goalNotes) profileSection += `\nStrategy notes from investor: ${profile.goalNotes}`;

  // B110 — household-scope derivations (multi-adult only). Mirrors the
  // DashboardPage pattern from B103 Phase 4c-1: when the household has
  // > 1 adult (primary + at least one non-dependent partner), the
  // cashflow aggregates below are emitted in HOUSEHOLD scope (your
  // share + partner's share combined) so the AI sees the same figures
  // the user reads on the Dashboard. Single-adult households are
  // bit-identical to pre-B110.
  const isMultiAdult = (profile.householdMembers ?? [])
    .some((m) => m.memberType !== "dependent");
  const householdFactorFn = (p: PropertyRecord): number => {
    const structure = structures.find((s) => s.id === p.structureId);
    return householdFactorOf(p, structure, profile);
  };

  // ─── Per-property tax position (budget-only, mirrors TaxPage propertyTaxData) ──
  // Figures are derived from the property record's budget fields and do not
  // include Year-End Reconciliation actuals (those live in TaxPage state and are
  // not loaded at the App.tsx level).
  const fyLabel = currentFYLabel();
  // B154.1 — stale-rates honesty guard. `rulesFY` is the FY of the loaded rate tables
  // (label only — no figure read). When the current FY has no rules file, prepend a
  // disclosure so the AI relays the caveat instead of asserting "calculated for FY<cur>".
  // Binds BOTH to direct FY/rates questions AND to volunteering any figure (the model
  // quoted tax-saving numbers unprompted in testing). "" in a normal year → dropped.
  const rulesFY = CURRENT_TAX_RULES.fyLabel;
  const taxRatesBasisDisclosure = fyLabel !== rulesFY
    ? `⚠️ TAX RATES BASIS — READ BEFORE STATING ANY FINANCIAL YEAR OR TAX FIGURE: the tax `
      + `brackets, Medicare levy, MLS and HELP figures in this context are computed using `
      + `FY${rulesFY} rates. The current financial year is FY${fyLabel}, but FY${fyLabel} `
      + `rates are NOT yet loaded in AusProp. Whenever you are asked what financial year or `
      + `what rates apply, OR whenever you state any tax bracket, Medicare/MLS/HELP, or `
      + `tax-saving figure, you MUST say the rates shown are FY${rulesFY} and that FY${fyLabel} `
      + `rates are pending — do NOT state that figures are "calculated for FY${fyLabel}". `
      + `The user's own entries (depreciation, sales) are FY-correct and unaffected.`
    : "";
  const propertyTaxData = activeProperties.map((prop) => {
    const ownershipFactor = (prop.ownershipPercent ?? 100) / 100;
    const structure = structures.find((s) => s.id === prop.structureId);
    // B106 — entityType retains the internal sentinel for the householdScope
    // dispatch; entityLabel uses the displayEntityLabel SSOT; isPersonal
    // routes through isPersonalProperty (structureId IS NULL).
    const entityType = structure?.entityType ?? "Personal";
    const entityLabel = displayEntityLabel(prop, structures);
    const isPersonal = isPersonalProperty(prop);
    const value = prop.currentValue ?? 0;
    const debt = totalLoanBalance(prop);
    // B129 — partial-FY pro-rata for FY-of-sale rental aggregation.
    // Active properties: factor = 1 (no behaviour change).
    // FY-of-sale: factor = daysHeld / 365 per saleProrate.ts.
    // Depreciation NOT pro-rated here — Wave 2C P11 handles that via
    // daysHeldThisFY in the Div 43/40 helpers; double-applying would halve.
    const proRata = prorateFactor(prop, fyLabel);
    const saleStatus = saleStatusForFY(prop, fyLabel);
    const rent = annualIncome(prop) * ownershipFactor * proRata;
    const opex = annualExpenses(prop) * ownershipFactor * proRata;
    const interest = annualLoanInterest(prop) * ownershipFactor * proRata;
    const repayment = annualLoanRepayment(prop);
    const totalRepayment = repayment.total * ownershipFactor;
    const deprRaw = annualDepreciation(prop, fyLabel);
    const depr = {
      total: deprRaw.total * ownershipFactor,
      div40: deprRaw.div40 * ownershipFactor,
      div43: deprRaw.div43 * ownershipFactor,
    };
    const totalDeductions = opex + interest + depr.total;
    const netRental = rent - totalDeductions;

    // ── CGT exposure (if sold at current market value, B130 Wave 2A) ──
    // Forward-looking only — sold properties skip this block; realised
    // CGT events from cgt_events table are deferred to B134.
    const isActive = prop.status !== "sold";

    const contractDate = prop.contractDate ?? prop.purchaseDate ?? null;
    const holdingPeriodDays = contractDate
      ? Math.floor((Date.now() - new Date(contractDate + "T00:00:00").getTime()) / 86400000)
      : null;
    const wouldQualifyDiscount = holdingPeriodDays !== null && holdingPeriodDays > 365;
    // B106 sentinel handling: this file uses `?? "Personal"` as an
    // internal dispatcher; cgtDiscountRate expects null for personal-name
    // per the B106 SSOT. Route through isPersonal.
    const discountRate = wouldQualifyDiscount && isActive
      ? cgtDiscountRate(isPersonal ? null : entityType)
      : 0;

    const capitalImprovementsTotal = (prop.capitalImprovements ?? [])
      .reduce((s, ci) => s + (ci.amount ?? 0), 0);
    // Mirror PropertyDetailPage Mark As Sold cost base formula: only
    // Div 43 reduces cost base per ATO s.110-45(3). Div 40 + "both" +
    // null divisionType excluded — they go through balancing
    // adjustments, not cost base reduction.
    const div43ClaimedTotal = (prop.depreciationClaimed ?? [])
      .filter((d) => d.divisionType === "43")
      .reduce((s, e) => s + (e.amountClaimed ?? 0), 0);
    const borrowingExpensesClaimedTotal = prop.totalBorrowingExpensesClaimed ?? 0;
    const costBaseLive = isActive
      ? (prop.purchasePrice ?? 0)
        + (prop.buyCosts ?? 0)
        + (prop.legalBuyCosts ?? 0)
        + capitalImprovementsTotal
        + borrowingExpensesClaimedTotal
        - div43ClaimedTotal
      : 0;

    const estimatedTaxableGainAtMarket = (() => {
      if (!isActive) return 0;
      const sellingCostsEstimated = (prop.currentValue ?? 0)
        * ((prop.estimatedSellingCostsPct ?? 2.5) / 100);
      const grossGain = (prop.currentValue ?? 0) - costBaseLive - sellingCostsEstimated;
      // Bug C parity: capital losses NOT discounted.
      if (grossGain <= 0) return grossGain;
      return grossGain * (1 - discountRate);
    })();

    const cgt = isActive ? {
      contractDate,
      holdingPeriodDays,
      wouldQualifyDiscount,
      discountRate,
      costBaseLive,
      capitalImprovementsTotal,
      div43ClaimedTotal,
      estimatedTaxableGainAtMarket,
    } : null;

    // ── Div 43 derivation status (B130 Wave 2B Phase 6) ──
    // Forward-looking derivation/sanity-check sibling to the existing
    // DEPRECIATION FY-block (which is lodged-return ground truth via
    // depreciationClaimed[]). Both blocks coexist per audit R1.5.
    // Sold properties skipped (CGT precedent).
    const div43 = isActive ? (() => {
      const schedule = prop.depreciationSchedule;
      if (!schedule) return { hasSchedule: false } as const;
      // L60 routing: assetClass-keyed (not propertyType). Commercial +
      // Industrial → non-residential; Residential + Mixed Use →
      // residential (Mixed Use defaults conservatively).
      const propertyClass: "residential" | "non-residential" =
        prop.assetClass === "Commercial" || prop.assetClass === "Industrial"
          ? "non-residential"
          : "residential";
      const commencedDate = schedule.div43DateCommenced ?? null;
      const completedDate = schedule.div43DateCompleted ?? null;
      const rateInput = commencedDate ?? completedDate ?? null;
      const rateResult = computeDiv43Rate(rateInput, propertyClass, {});
      const overridden = schedule.div43RateOverridden === true;
      const overrideRate = schedule.div43Rate;
      const claimResult = computeDiv43AnnualClaim(
        schedule,
        prop.contractDate ?? prop.purchaseDate ?? null,
        prop.saleContractDate ?? prop.saleDate ?? null,
        fyLabel,
        propertyClass,
        {},
      );
      const boundaryNearby = isDiv43BoundaryCase(completedDate);
      return {
        hasSchedule: true,
        propertyClass,
        commencedDate,
        completedDate,
        constructionCost: schedule.div43ConstructionCost ?? null,
        rateResult,
        overridden,
        overrideRate,
        claimAmount: claimResult.amount,
        daysHeldThisFY: claimResult.daysHeldThisFY,
        remainingYears: claimResult.remainingYears,
        boundaryNearby,
      } as const;
    })() : null;

    // ── Div 40 derivation status (B130 Wave 2C Phase 10) ──
    // Forward-looking derivation/sanity-check sibling to the existing
    // DEPRECIATION FY-block (lodged-return ground truth via depreciation-
    // Claimed[]). Per audit R4.5 + L63 — every override and absence emitted
    // explicitly. Sold properties skipped (CGT precedent — Wave 2A Phase 4).
    const div40 = isActive ? (() => {
      const schedule = prop.depreciationSchedule;
      if (!schedule || !schedule.div40Items || schedule.div40Items.length === 0) {
        return { hasItems: false } as const;
      }
      const ownerStructureForCalc = structure
        ? { excludedEntityType: structure.excludedEntityType ?? null }
        : null;
      const propertyContext = {
        assetClass: prop.assetClass,
        contractDate: prop.contractDate,
        purchaseDate: prop.purchaseDate ?? "",
        div43DateCompleted: schedule.div43DateCompleted,
      };
      const items = schedule.div40Items.map((item) => {
        const eligibility = computeDiv40Eligibility(item, propertyContext, ownerStructureForCalc);
        const claim = computeDiv40AnnualClaim(
          item,
          propertyContext,
          ownerStructureForCalc,
          fyLabel,
          prop.saleContractDate ?? null,
        );
        const fyOverride = item.fyOverrides?.find((o) => o.fy === fyLabel);
        return { item, eligibility, claim, fyOverride };
      });
      const pools = recomputeLowValuePools(schedule.div40Items);
      const poolThisFy = pools.find((p) => p.fy === fyLabel);
      const eligibleCount = items.filter((r) => r.eligibility.eligible).length;
      const blockedCount = items.length - eligibleCount;
      const totalAnnualDecline = items
        .filter((r) => !r.item.poolAllocated)
        .reduce((sum, r) => sum + (r.eligibility.eligible ? r.claim.amount : 0), 0);
      const blockedCostBaseFlow = items
        .filter((r) => !r.eligibility.eligible)
        .reduce((sum, r) => sum + r.item.cost, 0);
      return {
        hasItems: true,
        items,
        pools,
        poolThisFy,
        eligibleCount,
        blockedCount,
        totalAnnualDecline,
        blockedCostBaseFlow,
        ownerStructureExcludedEntity: structure?.excludedEntityType ?? null,
      } as const;
    })() : null;

    return { prop, value, debt, rent, opex, interest, totalRepayment, repayment, depr, netRental, entityType, entityLabel, isPersonal, ownershipFactor, cgt, div43, div40, saleStatus };
  });

  // Portfolio summary — when SMSF toggle is OFF, exclude SMSF from totals
  const isSMSFEntity = (entityType: string) => entityType === "SMSF";
  const summaryData = includeSMSFInPortfolio
    ? propertyTaxData
    : propertyTaxData.filter((d) => !isSMSFEntity(d.entityType));
  // B27 #1 + #4: portfolio totals (value, debt, equity, LVR) route through
  // portfolioSummary() SSOT — same path as Dashboard / Strategy / Reports.
  // Net cashflow (totalNetCashflow below) stays inline because it uses the
  // tax-rental form (rent − opex − interest, share-adjusted), which differs
  // from portfolioSummary().totalCashflow (Path A, P&I full repayment).
  const portfolioTotals = portfolioSummary(summaryData.map((d) => d.prop));
  const totalValue = portfolioTotals.totalValue;
  const totalDebt = portfolioTotals.totalDebt;
  let totalNetCashflow = 0;
  for (const d of summaryData) {
    totalNetCashflow += d.rent - d.opex - d.interest;
  }
  // B110 — household-scope tax-position aggregate (rent − opex − interest,
  // recomputed from raw with householdFactorFn instead of d.ownershipFactor).
  let totalNetCashflowHousehold = 0;
  for (const d of summaryData) {
    const f = householdFactorFn(d.prop);
    totalNetCashflowHousehold +=
      annualIncome(d.prop) * f - annualExpenses(d.prop) * f - annualLoanInterest(d.prop) * f;
  }

  const totalEquity = totalValue - totalDebt;
  const lvrPct = lvr(totalDebt, totalValue);
  // B27 #5 — Cash position annual via portfolioSummary SSOT (same as Dashboard).
  // Sums propertyMetrics(p).netCashflowShare across the same scope as the rest
  // of this context block, then × 12 to annualise.
  const cashAnnualCashflow = summaryData.reduce(
    (s, d) => s + propertyMetrics(d.prop).netCashflowShare * 12, 0
  );
  // B110 — household-scope cash position. Same SSOT, swap factor.
  const cashAnnualCashflowHousehold = summaryData.reduce(
    (s, d) => s + propertyMetrics(d.prop).netCashflow * householdFactorFn(d.prop) * 12, 0
  );

  // B110 — display selectors. Single-adult: legacy single-lens (back-compat).
  // Multi-adult: household scope. Tax-saving allocation stays per-adult and
  // is emitted in the Per-adult tax allocation section below.
  const displayTotalNetCashflow = isMultiAdult ? totalNetCashflowHousehold : totalNetCashflow;
  const displayCashAnnual = isMultiAdult ? cashAnnualCashflowHousehold : cashAnnualCashflow;

  // B122 P5 — Personal Tax Position aggregates. Strict `=== true` equality
  // on isPersonal per audit R3 #1. Trust/SMSF/company losses surface on the
  // Trusts branch (P2) and SMSF branch (P4); they are NOT aggregated here.
  const totalPersonalLoss = summaryData
    .filter((d) => d.netRental < 0 && d.isPersonal === true)
    .reduce((s, d) => s + Math.abs(d.netRental), 0);
  // Paper (depreciation) component, scoped to personal so paper + cash =
  // personal total exactly.
  const totalPersonalDeprComponent = summaryData
    .filter((d) => d.netRental < 0 && d.isPersonal === true)
    .reduce((s, d) => s + Math.min(d.depr.total, Math.abs(d.netRental)), 0);
  const totalPersonalCashComponent = totalPersonalLoss - totalPersonalDeprComponent;
  const estimatedTaxSaving = personalTaxSavingFromLoss(totalPersonalLoss, profile);
  const totalAnnualDepreciation = summaryData.reduce((s, d) => s + d.depr.total, 0);

  // B110 — after-tax line uses displayCashAnnual (household when multi-adult)
  // plus the existing single-lens estimatedTaxSaving. Tax-saving allocation
  // stays per-adult; the scope note below tells the AI to consult the
  // Per-adult tax allocation section for the household tax-saving breakdown.
  const afterTaxAnnualCashflow = displayCashAnnual + estimatedTaxSaving;
  const scopeNote = isMultiAdult
    ? "\nCashflow figures below are HOUSEHOLD scope (your share + partner's share combined). Tax-saving allocation remains per-adult — see Per-adult tax allocation section."
    : "";
  const summarySection = `PORTFOLIO SUMMARY
Properties: ${activeProperties.length} active
Total value: ${fmt(totalValue)}
Total debt: ${fmt(totalDebt)}
Total equity: ${fmt(totalEquity)}
LVR: ${lvrPct.toFixed(1)}%${scopeNote}
Net annual cashflow (tax position): ${fmt(displayTotalNetCashflow)}/yr — rent minus expenses minus interest only, share-adjusted, excludes principal repayments and depreciation.
After-tax cash position: ${fmt(afterTaxAnnualCashflow)}/yr — cash position plus expected refund on deductible loss, assumes refund eventually received (no PAYG variation modelled).
Cash position: ${fmt(displayCashAnnual)}/yr — what actually leaves the bank (full mortgage including principal), pre-refund. The Dashboard Net Cashflow KPI defaults to After-tax; toggle to Cash for pre-refund or Tax for the deductible-loss view.`;

  // Properties section — now includes inline net rental + entity quarantine label
  // and per-property equity release tranches when present
  const propertyLines: string[] = [];
  for (const d of propertyTaxData) {
    const equity = d.value - d.debt;
    const baseLabel = d.prop.nickname ?? d.prop.address ?? "Unknown";
    // B129 — annotate FY-of-sale rows so the AI sees the partial-FY context.
    const saleSuffix = d.saleStatus === "sold-this-fy"
      ? ` (partial FY — sold ${d.prop.saleContractDate ?? d.prop.saleDate})`
      : "";
    const label = `${baseLabel}${saleSuffix}`;
    // Net rental for this property's structure — determines whether
    // trust losses are quarantined or distributable
    const structureNetRental = propertyTaxData
      .filter((pd) => pd.prop.structureId === d.prop.structureId)
      .reduce((sum, pd) => sum + pd.netRental, 0);

    let quarantineLabel: string;
    if (d.isPersonal) {
      quarantineLabel = d.netRental < 0
        ? "Personal — deductible against personal income (negative gearing)"
        : "Personal — tax-deductible";
    } else if (d.entityType === "SMSF") {
      quarantineLabel = "SMSF — losses quarantined in fund (15% rate)";
    } else if (structureNetRental > 0) {
      quarantineLabel = `${d.entityType} — net positive, distributions taxable to beneficiaries`;
    } else {
      quarantineLabel = `${d.entityType} — net negative, losses quarantined in entity`;
    }
    const netRentalStr = d.netRental < 0
      ? `-${fmt(Math.abs(d.netRental))}`
      : fmt(d.netRental);
    propertyLines.push(
      `${label} (${d.prop.assetClass}, ${d.entityLabel}): value ${fmt(d.value)}, debt ${fmt(d.debt)}, equity ${fmt(equity)}, rent ${fmt(d.rent)}/yr, net rental: ${netRentalStr}/yr [${quarantineLabel}]`
    );
    propertyLines.push(`  loan interest (tax-deductible): ${fmt(d.interest)}/yr, total repayment (cashflow): ${fmt(d.totalRepayment)}/yr`);

    // N1 — historical growth from value/rent snapshots (when present)
    const valueCAGR = calculateCAGR(d.prop.valueHistory);
    const rentCAGR = calculateCAGR(d.prop.rentHistory);
    if (valueCAGR !== null || rentCAGR !== null) {
      const parts: string[] = [];
      if (valueCAGR !== null) parts.push(`value CAGR ${(valueCAGR * 100).toFixed(1)}%`);
      if (rentCAGR !== null) parts.push(`rent CAGR ${(rentCAGR * 100).toFixed(1)}%`);
      propertyLines.push(`  historical growth: ${parts.join(", ")} (from ${d.prop.valueHistory?.length ?? 0} value snapshots, ${d.prop.rentHistory?.length ?? 0} rent snapshots)`);
    }

    if (d.repayment.isInIoPeriod && d.repayment.ioExpiryDate) {
      const postIoDate = new Date(d.repayment.ioExpiryDate);
      postIoDate.setDate(postIoDate.getDate() + 1);
      const postIo = annualLoanRepayment(d.prop, postIoDate);
      propertyLines.push(`  IO expires ${d.repayment.ioExpiryDate} — repayment will increase from ${fmt(d.totalRepayment)}/yr to ${fmt(postIo.total * d.ownershipFactor)}/yr`);
    }

    // Equity release tranches — one indented child line per release with
    // destination entity, arrangement, deductibility flag, optional drawdown
    // date and notes. Omit entirely when there are no releases.
    const releases = d.prop.equityReleases ?? [];
    if (releases.length > 0) {
      const releaseCount = releases.length === 1 ? "1 release" : `${releases.length} releases`;
      propertyLines.push(`  equity releases: ${releaseCount}`);
      for (const r of releases) {
        const destStructure = structures.find((s) => s.id === r.destinationStructureId);
        const destLabel = destStructure
          ? (destStructure.displayName ?? destStructure.entityType)
          : "(no destination set)";
        const arrangementLabel = r.arrangement === "informal"
          ? "informal (interest non-deductible — s.8-1)"
          : "formal inter-entity loan";
        const dateStr = r.date ? `, drawn ${r.date}` : "";
        const notesStr = r.notes ? `, "${r.notes}"` : "";
        propertyLines.push(
          `    - ${fmt(r.amount ?? 0)} ${arrangementLabel} → ${destLabel}${dateStr}${notesStr}`
        );
      }
    }
  }

  const propertiesSection = propertyLines.length > 0
    ? `PROPERTIES\n${propertyLines.join("\n")}`
    : "PROPERTIES\nNo active properties.";

  // Depreciation section — only properties with logged depreciation for current FY
  const depreciationLines: string[] = [];
  for (const d of propertyTaxData) {
    if (d.depr.total <= 0) continue;
    const label = d.prop.nickname ?? d.prop.address ?? "Unknown";
    const method = d.prop.depreciationSchedule?.div40Method;
    const methodLabel = method === "diminishing-value"
      ? ", Div 40 method: diminishing value"
      : method === "prime-cost"
        ? ", Div 40 method: prime cost"
        : "";
    depreciationLines.push(
      `${label}: Div 43 capital works ${fmt(d.depr.div43)}/yr, Div 40 plant & equipment ${fmt(d.depr.div40)}/yr, total ${fmt(d.depr.total)}/yr${methodLabel}`
    );
  }

  const depreciationSection = depreciationLines.length > 0
    ? `DEPRECIATION (FY${fyLabel})\n${depreciationLines.join("\n")}\nTotal annual depreciation claimed across portfolio: ${fmt(totalAnnualDepreciation)}/yr`
    : `DEPRECIATION (FY${fyLabel})\nNo depreciation entries logged for this FY. The user may have a depreciation schedule set up but has not yet recorded a per-FY claim entry.`;

  // ── CGT exposure section (B130 Wave 2A Phase 4) ──
  // Forward-looking "if sold today" per active property + portfolio
  // total. Sold properties skipped (realised CGT in cgt_events table
  // is B134 follow-up).
  const cgtRowsData = propertyTaxData
    .filter((d) => d.cgt !== null);
  let cgtSection = "";
  if (cgtRowsData.length > 0) {
    const cgtLines: string[] = [];
    cgtLines.push(`CGT EXPOSURE (FY${fyLabel}, if sold at current market value)`);
    cgtLines.push("Property | Entity | Held | Discount | Cost base | Est. taxable gain");
    let portfolioTotalGain = 0;
    for (const d of cgtRowsData) {
      const cgt = d.cgt!;
      const label = d.prop.nickname ?? d.prop.address ?? "Unknown";
      const heldStr = cgt.holdingPeriodDays !== null
        ? `${cgt.holdingPeriodDays}d`
        : "no contract date";
      const discountStr = cgt.discountRate === 0
        ? "0% (no discount)"
        : cgt.discountRate === 0.5
          ? "50%"
          : `${(cgt.discountRate * 100).toFixed(2)}%`;
      const gainStr = cgt.estimatedTaxableGainAtMarket < 0
        ? `-${fmt(Math.abs(cgt.estimatedTaxableGainAtMarket))} (loss)`
        : fmt(cgt.estimatedTaxableGainAtMarket);
      cgtLines.push(
        `${label} | ${d.entityLabel} | ${heldStr} | ${discountStr} | ${fmt(cgt.costBaseLive)} | ${gainStr}`
      );
      portfolioTotalGain += cgt.estimatedTaxableGainAtMarket;
    }
    cgtLines.push("");
    cgtLines.push(`Portfolio total estimated taxable gain at current values: ${fmt(portfolioTotalGain)}`);
    cgtLines.push(`Cost base formula: purchase price + buy costs + legal buy costs + capital improvements + borrowing expenses claimed − Div 43 capital works claimed (Div 40 plant & equipment NOT subtracted per ATO s.110-45(3); flows through balancing adjustments instead).`);
    cgtLines.push(`Discount rates by entity: Personal/Trust 50%, SMSF 33.33% (1/3), Company 0%. Held = days since contract date (purchase contract, not settlement). 12mo discount eligibility requires >365 days.`);
    cgtSection = cgtLines.join("\n");
  }

  // ── DIVISION 43 STATUS section (B130 Wave 2B Phase 6) ──
  // Derivation/sanity-check sibling to the DEPRECIATION (FY...) block
  // above. The DEPRECIATION block is lodged-return ground truth (sourced
  // from depreciationClaimed[] entries the user has logged). This block
  // is forward-looking derivation: what AusProp computes given the
  // construction commenced + completed dates + cost. Both coexist per
  // audit R1.5 — accountant uses lodged-return; AI uses derivation for
  // sanity-check + answering "is this property's Div 43 set up correctly".
  // Sold properties skipped (CGT EXPOSURE precedent — Wave 2A Phase 4).
  // QS-report flag wiring deferred to B136.
  const div43Rows = propertyTaxData.filter((d) => d.div43 !== null);
  let div43Section = "";
  if (div43Rows.length > 0) {
    const div43Lines: string[] = [];
    div43Lines.push(`DIVISION 43 STATUS (FY${fyLabel}, derivation-aware sanity check — sibling to DEPRECIATION block below which is lodged-return ground truth)`);
    let portfolioFyEstimate = 0;
    let derivableCount = 0;
    for (const d of div43Rows) {
      const div43 = d.div43!;
      const label = d.prop.nickname ?? d.prop.address ?? "Unknown";
      if (div43Lines.length > 1) div43Lines.push(""); // blank line between properties
      div43Lines.push(`${label} (${d.entityLabel}, ${d.prop.assetClass})`);
      if (!div43.hasSchedule) {
        div43Lines.push(`  No depreciation schedule — Div 43 derivation requires construction cost + commenced date.`);
        continue;
      }
      div43Lines.push(`  Construction commenced: ${div43.commencedDate ?? "unknown"}`);
      div43Lines.push(`  Construction completed: ${div43.completedDate ?? "unknown"}`);
      div43Lines.push(`  Construction cost: ${div43.constructionCost && div43.constructionCost > 0 ? fmt(div43.constructionCost) : "not entered"}`);
      div43Lines.push(`  Derived rate: ${(div43.rateResult.rate * 100).toFixed(1)}% over ${div43.rateResult.lifeYears || "—"} years — ${div43.rateResult.bandLabel}`);
      div43Lines.push(`  Reason: ${div43.rateResult.reason}`);
      // Per L60/L62 discipline: always emit Override line (signal vs noise
      // trade-off favours explicit signal so the AI can confidently say
      // "this property is using the derived rate").
      if (div43.overridden && typeof div43.overrideRate === "number") {
        div43Lines.push(`  Override: manually set to ${(div43.overrideRate * 100).toFixed(1)}% — derived would be ${(div43.rateResult.rate * 100).toFixed(1)}%`);
      } else {
        div43Lines.push(`  Override: none`);
      }
      if (div43.rateResult.eligible) {
        div43Lines.push(`  Eligible: yes`);
        const remainingStr = div43.remainingYears > 0
          ? `${div43.remainingYears} years`
          : "fully written off";
        div43Lines.push(`  Remaining life: ${remainingStr}`);
        if (div43.claimAmount > 0) {
          div43Lines.push(`  Estimated FY${fyLabel} claim: ${fmt(div43.claimAmount)} (${div43.daysHeldThisFY} days held this FY)`);
          portfolioFyEstimate += div43.claimAmount;
          derivableCount += 1;
        } else {
          div43Lines.push(`  Estimated FY${fyLabel} claim: — (claim window empty: missing contract date, life ended, or sale before FY)`);
        }
      } else {
        div43Lines.push(`  Eligible: no — ${div43.rateResult.reason}`);
        div43Lines.push(`  Remaining life: n/a — not eligible`);
        div43Lines.push(`  Estimated FY${fyLabel} claim: —`);
      }
      div43Lines.push(`  QS report uploaded: not tracked yet (B136 — flag wiring deferred)`);
      if (div43.boundaryNearby && !div43.commencedDate) {
        div43Lines.push(`  Boundary case: yes — completion date is within ±12 months of a Div 43 rate boundary; commenced date required to confirm rate.`);
      } else {
        div43Lines.push(`  Boundary case: no`);
      }
    }
    div43Lines.push("");
    if (derivableCount > 0) {
      div43Lines.push(`Portfolio FY${fyLabel} Div 43 estimate: ${fmt(portfolioFyEstimate)} (sum of estimated annual claims across ${derivableCount} ${derivableCount === 1 ? "property" : "properties"} with viable derivation)`);
    } else {
      div43Lines.push(`No properties have a determinable Div 43 derivation yet — construction commenced dates need to be captured for accurate ATO-compliant figures.`);
    }
    div43Section = div43Lines.join("\n");
  }

  // ── DIVISION 40 STATUS section (B130 Wave 2C Phase 10) ──
  // Derivation/sanity-check sibling to DIVISION 43 STATUS + DEPRECIATION
  // (FY...) blocks. Per L63 — every override + absence emitted explicitly.
  // Sold properties skipped per CGT precedent. Pool override mechanic
  // deferred to Phase 11 (currently emits "Pool override: none" for L63
  // discipline). PPOR-converted-to-rental flag deferred — field not yet
  // on PropertyRecord (Phase 11 wires it; until then no emission per
  // L57 grep showing absence on the live type).
  const div40Rows = propertyTaxData.filter((d) => d.div40 !== null);
  let div40Section = "";
  if (div40Rows.length > 0) {
    const div40Lines: string[] = [];
    div40Lines.push(`DIVISION 40 STATUS (FY${fyLabel}, derivation-aware sanity check — sibling to DEPRECIATION block which is lodged-return ground truth)`);
    let portfolioAnnualDecline = 0;
    let portfolioPoolDecline = 0;
    let portfolioBlockedCostBaseFlow = 0;

    for (const d of div40Rows) {
      const div40 = d.div40!;
      const label = d.prop.nickname ?? d.prop.address ?? "Unknown";
      if (div40Lines.length > 1) div40Lines.push("");
      div40Lines.push(`${label} (${d.entityLabel}, ${d.prop.assetClass})`);

      if (!div40.hasItems) {
        div40Lines.push(`  No Div 40 items — picker not yet used for this property.`);
        div40Lines.push(`  Excluded-entity flag: ${d.div40 && "ownerStructureExcludedEntity" in d.div40 ? d.div40.ownerStructureExcludedEntity ?? "not set" : "not set"}`);
        div40Lines.push(`  Second-hand rule applies: ${secondHandRuleApplies(d.prop)}`);
        continue;
      }

      // Property-level context
      div40Lines.push(`  Total assets: ${div40.items.length} (${div40.eligibleCount} eligible, ${div40.blockedCount} blocked)`);
      div40Lines.push(`  Excluded-entity flag: ${div40.ownerStructureExcludedEntity ?? "not set"}`);
      div40Lines.push(`  Second-hand rule applies: ${secondHandRuleApplies(d.prop)}`);

      // Pool block
      if (div40.poolThisFy) {
        const poolClaim = computeLowValuePoolAnnualClaim(div40.poolThisFy);
        div40Lines.push(`  Low-value pool: opening ${fmt(div40.poolThisFy.openingBalance)}, additions ${fmt(div40.poolThisFy.additionsThisFY)}, decline ${fmt(poolClaim.decline)}, closing ${fmt(div40.poolThisFy.closingBalance)}`);
        div40Lines.push(`  Pool override: none`);
        portfolioPoolDecline += poolClaim.decline;
      } else {
        div40Lines.push(`  Low-value pool: none allocated for FY${fyLabel}`);
        div40Lines.push(`  Pool override: none`);
      }

      // Per-asset enumeration
      div40Lines.push(`  Items:`);
      for (const r of div40.items) {
        const exceptionTag = r.eligibility.eligible && r.eligibility.reason.toLowerCase().includes("exception") && r.item.secondHandExceptionReason
          ? `Eligible: ${r.item.secondHandExceptionReason}`
          : r.eligibility.eligible
            ? "Eligible"
            : `Blocked: ${r.eligibility.blockedBy ?? "rule"}`;
        const lifeStr = r.item.effectiveLifeOverridden
          ? `${r.item.effectiveLife}y override`
          : `${r.item.effectiveLife}y derived`;
        const claimAmount = r.eligibility.eligible && !r.item.poolAllocated ? r.claim.amount : 0;
        const overrideStr = r.fyOverride
          ? `manually set to ${fmt(r.fyOverride.claimAmount)} (source: ${r.fyOverride.source}) — derived would be ${fmt(r.claim.amount)}`
          : "none";
        const methodStr = r.item.method === "diminishing-value" ? "DV" : "PC";
        const pooledTag = r.item.poolAllocated ? " [POOLED]" : "";
        div40Lines.push(`    - ${r.item.name || "(unnamed)"}: ${fmt(r.item.cost)}, ${methodStr} (${lifeStr}), opening AV ${fmt(r.claim.openingAV)}, FY claim ${fmt(claimAmount)}, override: ${overrideStr}, status: ${exceptionTag}${pooledTag}`);
      }

      // Blocked items cost-base flow
      if (div40.blockedCount > 0) {
        const blockedList = div40.items
          .filter((r) => !r.eligibility.eligible)
          .map((r) => `${r.item.name || "(unnamed)"} ${fmt(r.item.cost)}`)
          .join(", ");
        div40Lines.push(`  Blocked items (cost flows to CGT cost base on disposal): ${blockedList}`);
      }

      portfolioAnnualDecline += div40.totalAnnualDecline;
      portfolioBlockedCostBaseFlow += div40.blockedCostBaseFlow;
    }

    div40Lines.push("");
    if (portfolioAnnualDecline > 0 || portfolioPoolDecline > 0) {
      div40Lines.push(`Portfolio FY${fyLabel} Div 40 estimate: ${fmt(portfolioAnnualDecline + portfolioPoolDecline)} (per-asset decline ${fmt(portfolioAnnualDecline)} + pool decline ${fmt(portfolioPoolDecline)})`);
    } else {
      div40Lines.push(`No properties have computable Div 40 decline this FY — items need acquisition dates and eligibility to derive.`);
    }
    if (portfolioBlockedCostBaseFlow > 0) {
      div40Lines.push(`Blocked-asset cost flowing to CGT cost base on disposal: ${fmt(portfolioBlockedCostBaseFlow)} across portfolio.`);
    }

    div40Section = div40Lines.join("\n");
  }

  // Personal Tax Position section (B122 P5 rename + scope tightening).
  //
  // B119 follow-up + B122 P5 — block strictly personal Schedule 7 framing.
  // Trust / SMSF / Company entity losses report on those entities' own
  // returns (TRT / SMSF Annual Return / CTR) and are NOT aggregated here.
  // Entity-level positions surface on the Trusts branch (P2) and SMSF
  // branch (P4); the AI emits separate blocks for those.
  // Per-adult tax saving lives in `perAdultTaxAllocationSection` below.
  let personalTaxPositionSection: string;
  if (totalPersonalLoss <= 0) {
    personalTaxPositionSection = `PERSONAL TAX POSITION (FY${fyLabel})\nPersonal Schedule 7 is positively geared or break-even — no rental losses across personal-name properties.`;
  } else {
    personalTaxPositionSection = `PERSONAL TAX POSITION (FY${fyLabel})
Personal rental loss (Schedule 7, household total): ${fmt(totalPersonalLoss)}/yr
Paper loss component (depreciation): ${fmt(totalPersonalDeprComponent)}/yr
Cash loss component (out of pocket): ${fmt(totalPersonalCashComponent)}/yr`;
  }

  // B103 Phase 4c-2 — Per-adult tax allocation. Emitted only when the
  // household has > 1 adult (primary + at least one non-dependent partner).
  // Single-adult households get the existing personalTaxPositionSection unchanged
  // — no extra noise in the AI prompt.
  // Issue 3 (Day 52) — coerce missing memberType to partner. Mirrors the
  // householdScope + DashboardPage pattern. Two member kinds in the data
  // model (partner / dependent); anything else (undefined, null, missing)
  // is a partner by definition.
  const partnerAdults = (profile.householdMembers ?? [])
    .filter((m) => m.memberType !== "dependent");
  const allAdults = [
    {
      id: "primary",
      label: profile.preferredName ?? profile.fullLegalName ?? "Primary",
    },
    ...partnerAdults.map((m) => ({
      id: m.id,
      label: m.fullLegalName || m.preferredName || "Partner",
    })),
  ];

  let perAdultTaxAllocationSection = "";
  if (allAdults.length > 1) {
    const adultLines = allAdults.map((adult) => {
      const allocatedLoss = computeLensAllocatedLoss(
        activeProperties,
        structures,
        profile,
        adult.id,
      );
      if (allocatedLoss === 0) {
        return `- ${adult.label}: $0/yr — no allocated personal loss this FY`;
      }
      const lensProfile = resolveLensProfile(profile, adult.id);
      const adultTaxSaving = personalTaxSavingFromLoss(allocatedLoss, lensProfile);
      const adultIncome = lensProfile.annualPreTaxIncome ?? 0;
      // B116 — family-MLS context derived from the household level (same for
      // every adult); each adult's bracket MTR + Medicare flags remain
      // per-person. Avoid recomputing per-iteration by reusing familyMlsCtx.
      const adultEffRate = effectiveMarginalRate(adultIncome, {
        medicareExempt: lensProfile.medicareExempt,
        medicareReduction: lensProfile.medicareReduction,
        hasPrivateHospitalCover: lensProfile.hasPrivateHospitalCover,
        medicareLevy: lensProfile.medicareLevy,
        hasHECS: lensProfile.hasHECS,
        ...familyMlsCtx,
      });
      const adultBracketRate = lensProfile.marginalTaxRate
        ?? calculateMarginalRate(adultIncome);
      // B109 — conditional rate-components suffix per adult; collapses to
      // bracket-only when no extra components apply (e.g. Medicare-exempt
      // + no HECS) so the AI sees a label that matches the underlying math.
      // B116 — same family-MLS context threaded through.
      const adultComponentsLabel = formatRateComponentsLabel(adultIncome, {
        medicareExempt: lensProfile.medicareExempt,
        medicareReduction: lensProfile.medicareReduction,
        hasPrivateHospitalCover: lensProfile.hasPrivateHospitalCover,
        medicareLevy: lensProfile.medicareLevy,
        hasHECS: lensProfile.hasHECS,
        ...familyMlsCtx,
      });
      const adultRateDisplay = adultComponentsLabel
        ? `${adultEffRate.toFixed(1)}% effective marginal rate (bracket ${adultBracketRate}% + ${adultComponentsLabel})`
        : `${adultBracketRate}% marginal rate`;
      return `- ${adult.label}: allocated personal rental loss ${fmt(allocatedLoss)}/yr, estimated tax saving ${fmt(adultTaxSaving)}/yr at ${adultRateDisplay}`;
    });
    perAdultTaxAllocationSection = `Per-adult tax allocation (FY${fyLabel}):
${adultLines.join("\n")}
Trust / SMSF / Company rental losses are quarantined within the entity and excluded from this allocation (ITAA 1997 Div 36, Schedule 2F).`;
  }

  // Structures section — includes per-structure net rental aggregate and
  // selfFundingOverride status so the AI can reason about manual overrides.
  const structureLines: string[] = [];
  for (const s of structures) {
    const bCount = s.beneficiaries?.length ?? 0;
    const bLabel = bCount === 1 ? "1 beneficiary" : `${bCount} beneficiaries`;
    // Sum net rental across this structure's properties (already
    // ownership-adjusted, includes depreciation — see propertyTaxData above).
    const structureNetRental = propertyTaxData
      .filter((d) => d.prop.structureId === s.id)
      .reduce((sum, d) => sum + d.netRental, 0);
    const calculatedSelfFunding = structureNetRental > 0;
    const override = s.selfFundingOverride;
    let selfFundingLabel: string;
    if (override === true) {
      selfFundingLabel = `self-funding: forced on (manual override; calculation says ${calculatedSelfFunding ? "yes" : "no"})`;
    } else if (override === false) {
      selfFundingLabel = `self-funding: forced off (manual override; calculation says ${calculatedSelfFunding ? "yes" : "no"})`;
    } else {
      selfFundingLabel = `self-funding: auto (currently ${calculatedSelfFunding ? "yes" : "no"})`;
    }
    const netRentalStr = structureNetRental < 0
      ? `-${fmt(Math.abs(structureNetRental))}`
      : fmt(structureNetRental);
    structureLines.push(
      `${s.displayName ?? s.entityType}: ${s.entityType}, ${bLabel}, net rental ${netRentalStr}/yr → ${selfFundingLabel}`
    );
    // SMSF-specific context for the AI (Day 37)
    if (s.entityType === "SMSF") {
      const contributions = s.monthlyContributions ?? 0;
      const netFundMonthly = structureNetRental / 12 + contributions;
      const netFundStr = netFundMonthly < 0
        ? `-${fmt(Math.abs(netFundMonthly))}`
        : fmt(netFundMonthly);
      structureLines.push(`  Fund type: Self-Managed Super Fund (SMSF)`);
      structureLines.push(`  Loan structure: LRBA (Limited Recourse Borrowing Arrangement) — the property is likely held via a bare trust/holding trust. The loan is limited recourse: the lender can only claim the SMSF asset securing the loan, not other fund assets. Contributions + rental income service the loan.`);
      if (contributions > 0) {
        structureLines.push(`  Monthly contributions: ${fmt(contributions)}/mo (member salary sacrifice + employer SG + non-concessional)`);
        structureLines.push(`  Net fund position after contributions: ${netFundStr}/mo`);
      }
      structureLines.push(`  Tax rate: 15% fund rate (not personal marginal rate)`);
      structureLines.push(`  Losses quarantined within fund — cannot offset personal income`);
    }

    // B121 — per-beneficiary distribution allocation. Skipped for SMSF
    // (statutory) and zero/negative net (loss quarantined per B120).
    // FY context = current AI FY.
    const distributionLines: string[] = [];
    if (s.entityType !== "SMSF" && structureNetRental > 0 && (s.beneficiaries?.length ?? 0) > 0) {
      const { allocations, undistributedAmount, carryForward } = allocateTrustNetIncome(s, fyLabel, structureNetRental);
      // B125 — annotate gross/applied/adjusted/remaining when carry-forward applied.
      if (carryForward.lossesAppliedThisFY > 0) {
        distributionLines.push(
          `    Gross net income: ${fmt(structureNetRental)}; prior loss applied: ${fmt(carryForward.lossesAppliedThisFY)}; adjusted: ${fmt(carryForward.adjustedNetIncome)}`,
        );
        for (const e of carryForward.perEntryApplied) {
          distributionLines.push(
            `      - FY${e.fy} loss: ${fmt(e.appliedThisFY)} applied (${fmt(e.remainingAfter)} remaining)`,
          );
        }
      }
      if (carryForward.totalCarriedAtEnd > 0) {
        distributionLines.push(
          `    Carry-forward at end of ${fyLabel}: ${fmt(carryForward.totalCarriedAtEnd)} (Div 36)`,
        );
      }
      for (const a of allocations) {
        const actualSuffix = a.actualDistributed !== undefined
          ? ` (actual lodged: ${fmt(a.actualDistributed)})`
          : "";
        const sourceLabel = a.source === "fy-entry" ? "" : ` [${a.source}]`;
        distributionLines.push(
          `    - ${a.beneficiaryName}: ${a.percent.toFixed(1)}% = ${fmt(a.amount)}/yr${actualSuffix}${sourceLabel}`,
        );
      }
      if (undistributedAmount > 0) {
        distributionLines.push(
          `    - Undistributed: ${fmt(undistributedAmount)}/yr (taxable to trustee at 47% per s.99A — out of scope V1)`,
        );
      }
    } else if (s.entityType === "SMSF" && structureNetRental > 0) {
      distributionLines.push(`    - SMSF member entitlement (statutory, ITAA 1997 Div 290) — net income retained at fund level`);
    } else if (structureNetRental < 0 && (s.beneficiaries?.length ?? 0) > 0) {
      distributionLines.push(`    - Trust loss: ${fmt(structureNetRental)}/yr quarantined (no distribution; B125 carry-forward)`);
    }
    if (distributionLines.length > 0) {
      structureLines.push("  Distributions:");
      structureLines.push(...distributionLines);
    }
  }

  const structuresSection = structureLines.length > 0
    ? `STRUCTURES\n${structureLines.join("\n")}`
    : "STRUCTURES\nNone configured.";

  // ── B122 P3 — Per-Personal received-side view of in-app trust distributions.
  // Inverse of STRUCTURES → Distributions (which is the trustee's view per
  // trust). FWFT and other out-of-app trusts NOT included — known gap
  // documented in the AI-emission footer.
  const personalDistLines: string[] = [];
  // Reuse partnerAdults / allAdults pattern computed below at line ~765 —
  // we need the same shape here. Inline to keep the section self-contained
  // (small duplication; cheaper than restructuring the surrounding code).
  const _p3PartnerAdults = (profile.householdMembers ?? []).filter(
    (m) => m.memberType !== "dependent",
  );
  const _p3AllAdults = [
    {
      id: "primary",
      label: profile.preferredName ?? profile.fullLegalName ?? "Primary",
    },
    ..._p3PartnerAdults.map((m) => ({
      id: m.id,
      label: m.fullLegalName || m.preferredName || "Partner",
    })),
  ];
  // B122-P3 v2 (Day 81): helper takes raw properties + structures and
  // builds its own trustee-scope (un-lensed) propertyTaxData internally.
  // Eliminates lens-scope leak class.
  for (const adult of _p3AllAdults) {
    const result = personalTrustDistributions(adult.id, {
      structures,
      properties: activeProperties,
      fy: fyLabel,
    });
    if (result.rows.length === 0) {
      personalDistLines.push(`${adult.label}: no in-app trust distributions received this FY`);
      continue;
    }
    personalDistLines.push(`${adult.label}: ${fmt(result.total)} received from ${result.rows.length} in-app trust${result.rows.length === 1 ? "" : "s"}`);
    for (const r of result.rows) {
      const sourceLabel = r.source === "fy-entry" ? "" : ` [${r.source}]`;
      const actualSuffix = r.actualDistributed !== undefined ? ` (actual lodged: ${fmt(r.actualDistributed)})` : "";
      personalDistLines.push(`  - ${r.trustName}: ${fmt(r.amount)} (${r.percent.toFixed(1)}%)${sourceLabel}${actualSuffix}`);
    }
  }
  const personalDistSection = personalDistLines.length > 0
    ? `PERSONAL TRUST DISTRIBUTIONS RECEIVED (FY${fyLabel})\n${personalDistLines.join("\n")}\nNote: distributions from trusts NOT modelled in AusProp (e.g. external share-investment trusts) are excluded — those must be entered manually on lodged returns.`
    : "";

  // ── B122 P4 — SMSF Fund Summary (per-fund AI emission).
  // Mirrors the lodged SMSF Annual Return §B/§C/§D shape at the 15%
  // concessional rate (s.295-385). Helper takes raw properties + structures
  // and builds its own trustee-scope rows (L71). Never invokes
  // resolveBeneficiaryDistribution — SMSF entitlement is statutory.
  const smsfLines: string[] = [];
  for (const s of structures) {
    if (s.entityType !== "SMSF") continue;
    const fund = smsfFundSummary(s.id, {
      properties: activeProperties,
      structures,
      fy: fyLabel,
    });
    if (!fund) continue;
    smsfLines.push(`Fund: ${fund.displayName} (FY${fund.fy}, accumulation phase)`);
    smsfLines.push(
      `  §B Assessable: rent ${fmt(fund.rent)}, interest income ${fmt(fund.interestIncome)} [not tracked], employer contributions ${fmt(fund.employerContributions)} [not tracked]`,
    );
    smsfLines.push(`  §B Total assessable: ${fmt(fund.assessableIncome)}`);
    smsfLines.push(
      `  §C Deductions: LRBA interest ${fmt(fund.loanInterest)}, property opex ${fmt(fund.propertyOpex)}, depreciation ${fmt(fund.depreciation)}`,
    );
    smsfLines.push(`  §C Total deductions: ${fmt(fund.totalDeductions)}`);
    const taxableStr = fund.taxableIncome < 0
      ? `-${fmt(Math.abs(fund.taxableIncome))}`
      : fmt(fund.taxableIncome);
    smsfLines.push(`  §D Taxable income: ${taxableStr}`);
    smsfLines.push(
      `  §D Tax @ 15% concessional (s.295-385): ${fmt(fund.tax)}${fund.taxableIncome < 0 ? " (loss carried forward at fund level)" : ""}`,
    );
    if (fund.members.length > 0) {
      smsfLines.push(`  Members (statutory entitlement, s.295-385):`);
      for (const m of fund.members) {
        smsfLines.push(`    - ${m.name}: ${m.distributionPercent.toFixed(1)}% statutory`);
      }
    }
    smsfLines.push(
      `  Known gaps: cash interest income, employer contributions, member balance tracking — not yet modelled in AusProp. Reconciliation against lodged SMSF Annual Return is structural (§B/§C/§D shape + 15% rate), not penny-perfect.`,
    );
  }
  const smsfSection = smsfLines.length > 0
    ? `SMSF FUND SUMMARY (FY${fyLabel})\n${smsfLines.join("\n")}`
    : "";

  // ── Buckets section (waterfall allocation, Phase 6c-3d) ──
  const bucketsLines: string[] = [];
  if (budgetSettings && budgetSettings.buckets.length > 0 && monthlySurplus !== undefined) {
    const allocations = computeBucketAllocations(budgetSettings.buckets, monthlySurplus);
    bucketsLines.push(`  Allocation order (top to bottom = funded first):`);
    for (const a of allocations) {
      const typeLabel = a.bucket.type === "property" ? " [PROPERTY PORTFOLIO — funds your investment property purchases]" : "";
      const status = a.fullyFunded
        ? `${fmt(a.actual)}/mo`
        : a.actual === 0
          ? `${fmt(0)}/mo UNFUNDED — surplus exhausted before this bucket (${fmt(a.requested)}/mo requested)`
          : `${fmt(a.actual)}/mo PARTIAL — ${fmt(a.shortfall)}/mo short of ${fmt(a.requested)}/mo requested`;
      bucketsLines.push(`  ${a.bucket.name}: ${status} (type: ${a.bucket.type})${typeLabel}`);
    }
    const totalRequested = budgetSettings.buckets.reduce((s, b) => s + b.monthlyAmount, 0);
    const totalActual = allocations.reduce((s, a) => s + a.actual, 0);
    bucketsLines.push(`  Total requested: ${fmt(totalRequested)}/mo`);
    bucketsLines.push(`  Total actually funded: ${fmt(totalActual)}/mo`);
    if (totalRequested > totalActual) {
      bucketsLines.push(`  Shortfall: ${fmt(totalRequested - totalActual)}/mo — surplus insufficient to cover all requested allocations`);
    }
  }
  const bucketsSection = bucketsLines.length > 0 ? `BUCKETS\n${bucketsLines.join("\n")}` : "";

  // ── Funding summary section ──
  let fundingSection = "";
  if (budgetSettings && appSettings) {
    const fs = portfolioFundingSummary(activeProperties, structures, budgetSettings, appSettings, profile, monthlySurplus ?? 0);
    const fundingLines = [
      `  Personal contribution (investment bucket): ${fmt(fs.personalContribution)}/mo`,
      `  Equity release drawdowns: ${fmt(fs.equityReleaseTotal)}/mo`,
      `  Tax refund (PAYG variation): ${fmt(fs.taxRefundTotal)}/mo`,
      `  Internal redistribution: ${fmt(fs.internalRedistribution)}/mo`,
      `  Total funding: ${fmt(fs.totalFunding)}/mo`,
      `  Total portfolio cost: ${fmt(fs.totalCost)}/mo`,
      `  Buffer (${appSettings.portfolioBufferPercent ?? 10}%): ${fmt(fs.bufferAmount)}/mo`,
      `  Total cost with buffer: ${fmt(fs.totalCostWithBuffer)}/mo`,
      fs.gap > 0
        ? `  GAP: -${fmt(fs.gap)}/mo SHORTFALL — portfolio costs more than current funding`
        : `  GAP: +${fmt(Math.abs(fs.gap))}/mo SURPLUS — funding exceeds portfolio cost`,
    ];
    if (budgetSettings?.spareIncomeAvailableForInvesting != null) {
      const rawSurplus = Math.max(0, monthlySurplus ?? 0);
      const allocs = computeBucketAllocations(budgetSettings.buckets ?? [], rawSurplus);
      const funded = allocs.reduce((s, a) => s + a.actual, 0);
      const autoUnallocated = Math.max(0, rawSurplus - funded);
      fundingLines.push(`  Spare income for investing: ${fmt(budgetSettings.spareIncomeAvailableForInvesting)}/mo (manual override; auto-derived value is ${fmt(autoUnallocated)}/mo)`);
    }
    fundingSection = `FUNDING SUMMARY\n${fundingLines.join("\n")}`;
  }

  // ── Equity release runway section ──
  const runwayLines: string[] = [];
  const rentGrowth = appSettings?.rentGrowthAssumption ?? 0.03;
  for (const p of activeProperties) {
    for (const r of p.equityReleases ?? []) {
      if (!r.drawdownAmount || r.drawdownAmount <= 0 || !r.creditLimit) continue;
      const simple = equityReleaseRunwaySimple(r);
      const dynamic = equityReleaseRunwayDynamic(r, 0, rentGrowth);
      const remaining = Math.max(0, r.creditLimit - (r.balance ?? 0));
      const freqLabel = r.drawdownFrequency === "weekly" ? "/wk" : r.drawdownFrequency === "monthly" ? "/mo" : "/fn";
      const status = simple !== null && simple < 6 ? "RED" : simple !== null && simple < 12 ? "AMBER" : "GREEN";
      runwayLines.push(`  ${p.nickname ?? p.address} — ${r.notes ?? r.id.slice(0, 6)}: ${r.arrangement}`);
      runwayLines.push(`    Credit limit: ${fmt(r.creditLimit)}, balance: ${fmt(r.balance ?? 0)}, remaining: ${fmt(remaining)}`);
      runwayLines.push(`    Drawdown: ${fmt(r.drawdownAmount)}${freqLabel}`);
      if (simple !== null) runwayLines.push(`    Simple runway: ${Math.floor(simple / 12)}yr ${Math.round(simple % 12)}mo`);
      if (dynamic !== null) runwayLines.push(`    Dynamic runway (${Math.round(rentGrowth * 100)}% rent growth): ${Math.floor(dynamic / 12)}yr ${Math.round(dynamic % 12)}mo`);
      runwayLines.push(`    Status: ${status}`);
    }
  }
  const runwaySection = runwayLines.length > 0 ? `EQUITY RELEASE RUNWAY\n${runwayLines.join("\n")}` : "";

  // ── Redistribution policy section ──
  const redistLines: string[] = [];
  for (const s of structures) {
    const mode = s.redistributionMode ?? "hold";
    const modeLabel = mode === "redistribute" ? "surplus returned to personal" : mode === "pay_down_debt" ? "surplus applied to facility reduction" : "surplus retained at entity level";
    redistLines.push(`  ${s.displayName || s.entityType}: ${mode} — ${modeLabel}`);
  }
  const redistSection = redistLines.length > 0 ? `REDISTRIBUTION POLICY\n${redistLines.join("\n")}` : "";

  // ── Market intelligence section (cached HTAG data only — no fetches) ──
  const htagLines: string[] = [];
  for (const p of activeProperties) {
    const rawType = p.propertyType ?? p.assetClass ?? "house";
    const ht = getHtagType(rawType);
    if (!p.lgaId || !ht) continue;
    const cached = getCachedHtagData(p.lgaId, ht);
    if (!cached) continue;

    const pctFmt = (v: number) => `${(v * 100).toFixed(1)}%`;
    const s = cached.scores;
    const c = cached.cycle;
    const g = cached.growth;
    const d = cached.demand;
    const su = cached.supply;
    const sm = cached.summary;

    htagLines.push(`  ${p.nickname ?? p.address} (${p.suburb}, ${p.state}):`);
    htagLines.push(`    Data as of: ${cached.meta.period_end}`);
    htagLines.push(`    Cycle position: ${c.phase} (projected growth ${pctFmt(c.growth_low)} to ${pctFmt(c.growth_high)} p.a., rent growth ${pctFmt(c.rent_growth)} p.a.)`);
    htagLines.push(`    RCS overall: ${Math.round(s.overall)}/100 (capital growth ${Math.round(s.capital_growth)}, cashflow ${Math.round(s.cashflow)}, lower risk ${Math.round(s.lower_risk)})`);
    htagLines.push(`    Price CAGR: 1yr ${pctFmt(g.price_1y)}, 3yr ${pctFmt(g.price_3y)}, 5yr ${pctFmt(g.price_5y)}, 10yr ${pctFmt(g.price_10y)}`);
    htagLines.push(`    Rent CAGR: 1yr ${pctFmt(g.rent_1y)}, 3yr ${pctFmt(g.rent_3y)}, 5yr ${pctFmt(g.rent_5y)}, 10yr ${pctFmt(g.rent_10y)}`);
    htagLines.push(`    Demand: DOM ${Math.round(d.dom)} days, vacancy ${pctFmt(d.vacancy_rate)}, clearance ${pctFmt(d.clearance_rate)}`);
    htagLines.push(`    Supply: stock on market ${pctFmt(su.stock_on_market)}, inventory ${su.inventory.toFixed(1)} months, hold period ${su.hold_period.toFixed(1)} yrs`);
    if (sm && sm.typical_price > 0) {
      htagLines.push(`    LGA benchmarks: typical price ${fmt(sm.typical_price)}, rent $${Math.round(sm.weekly_rent)}/wk, gross yield ${pctFmt(sm.gross_yield)}`);
    }
    htagLines.push(`    Risk: flood ${cached.risk.flood_percentile}th pctile, fire ${cached.risk.fire_percentile}th pctile, EDI ${cached.risk.edi}/100`);
    if (cached.estimate?.estimate_mid) {
      htagLines.push(`    AVM estimate: ${fmt(cached.estimate.estimate_mid)} (range ${cached.estimate.estimate_low ? fmt(cached.estimate.estimate_low) : "—"} – ${cached.estimate.estimate_high ? fmt(cached.estimate.estimate_high) : "—"})`);
    }
  }
  const htagSection = htagLines.length > 0
    ? `MARKET INTELLIGENCE (HTAG)\nCached from Market Intelligence panel — only properties whose panel was expanded this session are listed. See 'Data as of' for freshness.\n${htagLines.join("\n")}`
    : "";

  const footerNote = `NOTE\nFigures are budget estimates from each property record. Reconciled actual figures appear in the Tax page Year-End Reconciliation tab and may differ.`;

  return [
    taxRatesBasisDisclosure,   // B154.1 — first element; "" in a normal year → filter(Boolean) drops it
    profileSection,
    summarySection,
    propertiesSection,
    // B130 Wave 2B Phase 6 — order: CGT EXPOSURE → DIVISION 43 STATUS →
    // DEPRECIATION (FY) → PERSONAL TAX POSITION. Pre-Phase-6 the array had
    // depreciationSection ahead of cgtSection (Wave 2A Phase 4 inserted
    // cgtSection after depreciationSection). Phase 6 reorders so the
    // forward-looking derivation blocks (CGT exposure, Div 43 derivation)
    // sit ahead of the lodged-return ground-truth block (DEPRECIATION),
    // matching the design §5 narrative flow. B122 P5 renamed
    // negGearingSection → personalTaxPositionSection (same array slot per
    // audit R11 #9 — no AI prompt reordering).
    cgtSection,
    div43Section,
    // B130 Wave 2C Phase 10 — DIVISION 40 STATUS sits adjacent to
    // DIVISION 43 STATUS so the AI can reason about both forward-
    // looking depreciation paths together. DEPRECIATION (FY) below
    // remains the lodged-return ground truth (L48).
    div40Section,
    depreciationSection,
    personalTaxPositionSection,
    perAdultTaxAllocationSection,
    structuresSection,
    personalDistSection,
    smsfSection,
    bucketsSection,
    fundingSection,
    runwaySection,
    redistSection,
    htagSection,
    footerNote,
  ].filter(Boolean).join("\n\n");
}
